import { Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  DEFAULT_FILTERS,
  DEFAULT_SORTING,
  TemplateBase,
  TemplatesDisplay,
  TemplatesDisplayProps,
} from "./TemplatesDisplay.js";
import { useTemplatesQueryVariablesInState } from "./useTemplatesQueryVariables.js";
import { searchQuotesSortingToQuoteTemplatesSorting } from "../helpers.js";
import {
  TemplateSearchFiltersInput,
  TemplateSearchSortingInput,
} from "../../../../clients/graphqlTypes.js";

export const TemplateSelectModal = ({
  handleClose,
  handleComplete,
  title,
  initialFilters,
  initialSorting,
  fixedFilters,
}: Pick<TemplatesDisplayProps, "fixedFilters"> & {
  handleClose: () => void;
  handleComplete: (template: TemplateBase, handleClose: () => void) => void;
  title: string;
  initialFilters: TemplateSearchFiltersInput;
  initialSorting: TemplateSearchSortingInput[];
}) => {
  const { t } = useTranslate(["Templates", "Global"]);

  const [selectedTemplate, setSelectedTemplate] =
    React.useState<TemplateBase | null>(null);

  const {
    queryBaseVariables,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    paginationModel,
    setPaginationModel,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInState({
    initialFilters: initialFilters ?? DEFAULT_FILTERS,
    initialSorting: searchQuotesSortingToQuoteTemplatesSorting(
      initialSorting ?? DEFAULT_SORTING
    ),
    initialSearchTerm: "",
  });

  return (
    <Modal
      title={title}
      maxWidth="xl"
      fixedHeight
      // Without this, the modal-stack registration effect makes it render
      // display:none on its first frame, which silently defeats MUI's
      // one-shot auto-focus — Escape then does nothing until the user
      // clicks something inside to focus it manually.
      notInStack
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label: t("Select", { ns: "Global" }),
          handleClick: () => {
            if (!selectedTemplate) {
              throw new Error("Template missing");
            }
            handleComplete(selectedTemplate, handleClose);
          },
          buttonProps: {
            disabled: !selectedTemplate,
          },
        },
      ]}
      handleClose={handleClose}
    >
      <TemplatesDisplay
        fixedFilters={fixedFilters}
        queryBaseVariables={queryBaseVariables}
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        sorting={sorting}
        setSorting={setSorting}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        onInspectedTemplateClick={template => {
          handleComplete(template, handleClose);
        }}
      />
    </Modal>
  );
};
